import React, { useState } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import StorageIcon from '@mui/icons-material/Storage';

const ConnectorsSection: React.FC = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  // Exact list of 59 connectors from user specification
  const connectors = [
    { name: 'Airtable', isEnterprise: false },
    { name: 'Amazon Ads', isEnterprise: false },
    { name: 'Amazon Seller Partner', isEnterprise: false },
    { name: 'Amplitude', isEnterprise: false },
    { name: 'Azure Blob Storage', isEnterprise: false },
    { name: 'Bing Ads', isEnterprise: false },
    { name: 'Chargebee', isEnterprise: false },
    { name: 'Facebook Marketing', isEnterprise: false },
    { name: 'File (CSV, JSON, Excel, Feather, Parquet)', isEnterprise: false },
    { name: 'Freshdesk', isEnterprise: false },
    { name: 'GitHub', isEnterprise: false },
    { name: 'Gitlab', isEnterprise: false },
    { name: 'Google Ads', isEnterprise: false },
    { name: 'Google Analytics 4 (GA4)', isEnterprise: false },
    { name: 'Google Cloud Storage (GCS)', isEnterprise: false },
    { name: 'Google Search Console', isEnterprise: false },
    { name: 'Google Sheets', isEnterprise: false },
    { name: 'Harvest', isEnterprise: false },
    { name: 'HubSpot', isEnterprise: false },
    { name: 'IBM DB2', isEnterprise: true },
    { name: 'Instagram', isEnterprise: false },
    { name: 'Intercom', isEnterprise: false },
    { name: 'Jira', isEnterprise: false },
    { name: 'Klaviyo', isEnterprise: false },
    { name: 'LinkedIn Ads', isEnterprise: false },
    { name: 'Mailchimp', isEnterprise: false },
    { name: 'Microsoft SharePoint', isEnterprise: false },
    { name: 'Microsoft SQL Server (MSSQL)', isEnterprise: false },
    { name: 'Mixpanel', isEnterprise: false },
    { name: 'Monday', isEnterprise: false },
    { name: 'MongoDb', isEnterprise: false },
    { name: 'MySQL', isEnterprise: false },
    { name: 'Netsuite', isEnterprise: true },
    { name: 'Notion', isEnterprise: false },
    { name: 'Oracle', isEnterprise: true },
    { name: 'Paypal Transaction', isEnterprise: false },
    { name: 'Pinterest', isEnterprise: false },
    { name: 'Postgres', isEnterprise: false },
    { name: 'S3', isEnterprise: false },
    { name: 'Salesforce', isEnterprise: false },
    { name: 'Salesforce Marketing Cloud', isEnterprise: true },
    { name: 'SAP HANA', isEnterprise: true },
    { name: 'Sendgrid', isEnterprise: false },
    { name: 'Sentry', isEnterprise: false },
    { name: 'ServiceNow', isEnterprise: true },
    { name: 'Sharepoint Enterprise', isEnterprise: true },
    { name: 'Shopify', isEnterprise: false },
    { name: 'Slack', isEnterprise: false },
    { name: 'Snapchat Marketing', isEnterprise: false },
    { name: 'Snowflake', isEnterprise: false },
    { name: 'Stripe', isEnterprise: false },
    { name: 'TikTok Marketing', isEnterprise: false },
    { name: 'Twilio', isEnterprise: false },
    { name: 'Typeform', isEnterprise: false },
    { name: 'WooCommerce', isEnterprise: false },
    { name: 'Workday', isEnterprise: true },
    { name: 'Zendesk Chat', isEnterprise: false },
    { name: 'Zendesk Support', isEnterprise: false },
    { name: 'Zendesk Talk', isEnterprise: false },
  ];

  const filteredConnectors = connectors.filter(connector =>
    connector.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Box
      component="section"
      id="connectors"
      sx={{
        py: { xs: 8, md: 12 },
        backgroundColor: '#F8FAFC',
        textAlign: 'center',
      }}
    >
      <Container maxWidth="lg">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
          transition={{ duration: 0.8 }}
        >
          <Typography
            variant="h2"
            align="center"
            gutterBottom
            sx={{
              mb: 2,
              fontWeight: 800,
              fontSize: { xs: '2rem', md: '2.8rem' },
              color: '#0F172A',
              letterSpacing: '-0.02em',
            }}
          >
            {t('connectors.title')}
          </Typography>
          <Typography
            variant="h5"
            align="center"
            sx={{
              mb: 6,
              color: '#64748B',
              maxWidth: '800px',
              mx: 'auto',
              lineHeight: 1.6,
            }}
          >
            {t('connectors.subtitle')}
          </Typography>

          {/* Main CTA Button */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <Button
              variant="contained"
              size="large"
              onClick={handleOpen}
              startIcon={<StorageIcon />}
              sx={{
                py: 1.6,
                px: 5,
                fontSize: '1.05rem',
                fontWeight: 700,
                backgroundColor: '#0F172A',
                color: 'white',
                borderRadius: '50px',
                textTransform: 'none',
                boxShadow: '0 6px 20px rgba(15, 23, 42, 0.20)',
                '&:hover': {
                  backgroundColor: '#1E293B',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 12px 28px rgba(15, 23, 42, 0.28)',
                },
              }}
            >
              {t('connectors.viewConnectors')}
            </Button>
          </motion.div>

          {/* Quick stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <Typography
              variant="body1"
              sx={{
                mt: 4,
                color: '#64748B',
                fontSize: '1.1rem',
              }}
            >
              <strong>{connectors.length}</strong> {t('connectors.totalConnectors')}
            </Typography>
          </motion.div>
        </motion.div>

        {/* Connectors Modal */}
        <Dialog
          open={open}
          onClose={handleClose}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              maxHeight: '90vh',
            }
          }}
        >
          <DialogTitle sx={{ pb: 1, pr: 6 }}>
            <Typography variant="h4" fontWeight="bold">
              WAI Conectores
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {connectors.length} conectores disponibles
            </Typography>
            <IconButton
              aria-label="close"
              onClick={handleClose}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: 'grey.500',
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent>
            {/* Search */}
            <TextField
              fullWidth
              placeholder={t('connectors.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 3 }}
            />

                         {/* Connectors Grid */}
             <Box
               sx={{
                 display: 'grid',
                 gridTemplateColumns: {
                   xs: '1fr',
                   sm: 'repeat(2, 1fr)',
                   md: 'repeat(3, 1fr)',
                 },
                 gap: 2,
               }}
             >
               {filteredConnectors.map((connector, index) => (
                 <Box
                   key={index}
                   sx={{
                     p: 2,
                     border: '1px solid #E2E8F0',
                     borderRadius: 2,
                     display: 'flex',
                     justifyContent: 'space-between',
                     alignItems: 'center',
                     '&:hover': {
                       backgroundColor: '#F8FAFC',
                       borderColor: '#0F172A',
                     }
                   }}
                 >
                   <Typography variant="body2" fontWeight="medium">
                     {connector.name}
                   </Typography>
                   {connector.isEnterprise && (
                     <Chip 
                       label="ENTERPRISE" 
                       size="small" 
                       sx={{ 
                         backgroundColor: '#FEF3C7',
                         color: '#F59E0B',
                         fontSize: '0.7rem',
                         fontWeight: 'bold'
                       }} 
                     />
                   )}
                 </Box>
               ))}
             </Box>

            {filteredConnectors.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  No se encontraron conectores con "{searchTerm}"
                </Typography>
              </Box>
            )}
          </DialogContent>

          <DialogActions sx={{ p: 3, pt: 1 }}>
            <Box sx={{ width: '100%', textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('connectors.buildOwn')}
              </Typography>
              <Button
                variant="outlined"
                component="a"
                href={`mailto:info@waiworking.com?subject=${encodeURIComponent(t('connectors.contactSubject'))}`}
                onClick={handleClose}
                sx={{
                  borderColor: '#0F172A',
                  color: '#0F172A',
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: '#0F172A',
                    color: 'white',
                    borderColor: '#0F172A',
                  }
                }}
              >
                {t('connectors.contactUs')}
              </Button>
            </Box>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default ConnectorsSection; 